import { CaretDownIcon } from '@phosphor-icons/react'
import { useMemo } from 'react'
import type { FindingAggregateResponseStatistics, Scale } from '../../utils/api-client/api-schemas.ts'
import { getSeveritiesByRankDescending } from '../../utils/scale-utils.ts'
import { pluralize } from '../../utils/text-utils.ts'
import { OutlineButton } from '../buttons/outline-button.tsx'
import { DropdownFilter, type DropdownElement, type Filter } from '../dropdown-filter.tsx'
import * as styles from './resolution-scope-control.css.ts'
import {
  countScopeExclusions,
  EMPTY_RESOLUTION_SCOPE,
  FIX_OUTCOME_KEYS,
  FIX_OUTCOME_LABELS,
  getFixOutcomeCounts,
  getTriageOutcomeCounts,
  toggleScopeExclusion,
  TRIAGE_OUTCOME_KEYS,
  TRIAGE_OUTCOME_LABELS,
  type ResolutionScope,
  type ScopeGroup,
} from './resolution-scope.ts'

export type ResolutionScopeControlProps = {
  scope: ResolutionScope
  onScopeChange: (scope: ResolutionScope) => unknown
  /** Counts shown beside each outcome. Omitted while the aggregate query is still running. */
  statistics?: FindingAggregateResponseStatistics
  /** The scan's severity scale, which names the severity rows. */
  scale?: Scale
  disabled?: boolean
}

/**
 * The "Data scope" control in the panel's head: which findings the panel counts and the table
 * lists.
 *
 * Checked means in scope, so the menu opens with everything ticked and the user creates exclusions
 * by unticking — matching the design's "Deselect to create exclusions". That is the inverse of the
 * table's own Filter menu, where ticking a box narrows, so the two are deliberately separate
 * controls over separate state rather than one shared menu.
 */
export const ResolutionScopeControl: React.FC<ResolutionScopeControlProps> = ({
  scope,
  onScopeChange,
  statistics,
  scale,
  disabled,
}) => {
  const severityLabels = useMemo(() => getSeveritiesByRankDescending(scale).map(severity => severity.label), [scale])

  const filterElements = useMemo((): DropdownElement[] => {
    const triageCounts = statistics ? getTriageOutcomeCounts(statistics) : undefined
    const fixCounts = statistics ? getFixOutcomeCounts(statistics) : undefined

    const toGroup = ({
      group,
      text,
      keys,
      labels,
      counts,
    }: {
      group: ScopeGroup
      text: string
      keys: readonly string[]
      labels: Record<string, string>
      counts?: Record<string, number>
    }): DropdownElement => {
      const excluded: string[] = scope[group]
      const filters: Filter[] = keys.map(key => ({
        type: 'checkbox',
        id: `scope-${group}-${key}`,
        text: labels[key] ?? key,
        value: !excluded.includes(key),
        count: counts?.[key],
        onToggle: () => onScopeChange(toggleScopeExclusion({ scope, group, value: key })),
      }))

      return {
        type: 'group',
        id: `scope-${group}`,
        text,
        filters,
        // Matches the group checkbox's own indeterminate-to-off behaviour: while anything is still
        // in scope the group turns everything off, and once nothing is left it turns all back on.
        onToggleGroup: () => {
          const isAnyIncluded = keys.some(key => !excluded.includes(key))

          return onScopeChange({ ...scope, [group]: isAnyIncluded ? [...keys] : [] })
        },
      }
    }

    const severityGroup =
      severityLabels.length > 0
        ? toGroup({
            group: 'severityLabels',
            text: 'Severity',
            keys: severityLabels,
            labels: Object.fromEntries(severityLabels.map(label => [label, label])),
          })
        : undefined

    const elements: (DropdownElement | undefined)[] = [
      toGroup({
        group: 'triageOutcomes',
        text: 'Triage outcomes',
        keys: TRIAGE_OUTCOME_KEYS,
        labels: TRIAGE_OUTCOME_LABELS,
        counts: triageCounts,
      }),
      { type: 'divider', id: 'scope-divider-fix' },
      toGroup({
        group: 'fixOutcomes',
        text: 'Fix outcomes',
        keys: FIX_OUTCOME_KEYS,
        labels: FIX_OUTCOME_LABELS,
        counts: fixCounts,
      }),
      severityGroup ? { type: 'divider', id: 'scope-divider-severity' } : undefined,
      severityGroup,
    ]

    return elements.filter((element): element is DropdownElement => element !== undefined)
  }, [scope, onScopeChange, statistics, severityLabels])

  const exclusionCount = countScopeExclusions(scope)

  return (
    <DropdownFilter
      filterElements={filterElements}
      label="Data scope"
      align="end"
      disabled={disabled}
      isAnyNonDefaultFilterActive={exclusionCount > 0}
      onToggleAllFiltersOff={() => onScopeChange(EMPTY_RESOLUTION_SCOPE)}
      resetLabel="Reset scope to all"
      trigger={() => (
        <OutlineButton state={exclusionCount > 0 ? 'active' : 'inactive'} size="small" aria-label="Data scope">
          <span className={styles.triggerLabel}>Data scope:</span>
          <span className={styles.triggerValue}>
            {exclusionCount > 0 ? `${exclusionCount} ${pluralize(exclusionCount, 'exclusion', 'exclusions')}` : 'All'}
          </span>
          <CaretDownIcon size={14} weight="fill" />
        </OutlineButton>
      )}
    />
  )
}
